// Inline label + value dropdown — mirrors src/components/common/SortDropdown.tsx.
// Renders as "<label>: <bold value> ▾" with no surrounding border or
// background, so it reads as a control inside a card rather than a form input.
//
// Click → popover with options. Click outside or pick option → closes.

import React, {useEffect, useRef, useState} from 'react'
import clsx from 'clsx'

interface DropdownOption {
  label: string
  value: string
}

interface DropdownProps {
  label: string
  options: readonly DropdownOption[]
  value: string
  onChange: (value: string) => void
}

export const Dropdown: React.FC<DropdownProps> = ({label, options, value, onChange}) => {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)

  const selectedLabel = options.find(o => o.value === value)?.label ?? value

  // Close on outside click.
  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node
      if (
        triggerRef.current && !triggerRef.current.contains(t) &&
        popoverRef.current && !popoverRef.current.contains(t)
      ) {
        setOpen(false)
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-xs text-md outline-none"
      >
        <span className="text-textSecondary">{label}:</span>
        <span className="font-semibold text-textPrimary">{selectedLabel}</span>
        <span className="text-sm text-textPrimary">▾</span>
      </button>

      {open && (
        <div
          ref={popoverRef}
          className="absolute left-0 top-full z-30 mt-xs min-w-[200px] rounded-lg border border-surfaceBorder bg-surface p-xs shadow-lg"
        >
          {options.map(opt => {
            const selected = opt.value === value
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  onChange(opt.value)
                  setOpen(false)
                }}
                className={clsx(
                  'block w-full rounded-md px-lg py-md text-left text-md',
                  selected
                    ? 'bg-primaryBg font-semibold text-primary'
                    : 'text-textPrimary hover:bg-surfaceLight',
                )}
              >
                {opt.label}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
